import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';

// Import the generated protobuf types and gRPC clients
import { ArchitecturalPlanWriteServiceClient } from '../../generated.commonjs/ApiServiceClientPb';
import { DeleteArchitecturalPlanRequest, DeleteArchitecturalPlanResponse } from '../../generated.commonjs/api_pb';

export interface ProjectDeleteOptions {
  projectId: string;
  confirmationDeleteText: string;
  confirmationProjectId: string;
  forceHardDelete?: boolean;
}

export interface ProjectDeleteResult {
  success: boolean;
  projectId: string;
  filesDeleted?: number;
  bytesDeleted?: number;
  deletedFilePaths?: string[];
  hardDeleted?: boolean;
  message?: string;
  error?: string;
}

/** Handles project deletion workflows including file cleanup, confirmation, and navigating away after removal. */
@Injectable({
  providedIn: 'root',
})
export class ProjectDeleteService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);

  private client: ArchitecturalPlanWriteServiceClient;

  constructor() {
    const apiServer = this.environmentService.getApiServer();
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };
    this.client = new ArchitecturalPlanWriteServiceClient(`https://${apiServer}:443`, null, options);
  }

  /**
   * Delete a project with the specified confirmation options
   */
  deleteProject(options: ProjectDeleteOptions): Observable<ProjectDeleteResult> {
    ClientLoggingService.log('INFO', '🗑️ ProjectDeleteService: Initiating project deletion', options);

    // Create the gRPC request
    const request = new DeleteArchitecturalPlanRequest();
    request.setProjectId(options.projectId);
    request.setConfirmationDeleteText(options.confirmationDeleteText);
    request.setConfirmationProjectId(options.confirmationProjectId);
    request.setForceHardDelete(options.forceHardDelete || false);

    ClientLoggingService.log('INFO', '🗑️ ProjectDeleteService: Created gRPC request', {
      projectId: request.getProjectId(),
      confirmationDeleteText: request.getConfirmationDeleteText(),
      confirmationProjectId: request.getConfirmationProjectId(),
    });

    // Make the gRPC call (authentication is handled by the interceptor)
    const grpcCall = this.client.deleteArchitecturalPlan(request, null);

    return from(grpcCall).pipe(
      map((response: DeleteArchitecturalPlanResponse) => {
        ClientLoggingService.log('INFO', '🗑️ ProjectDeleteService: Processing successful response', response);

        const result: ProjectDeleteResult = {
          success: response.getSuccess(),
          projectId: response.getProjectId(),
          filesDeleted: response.getFilesDeleted(),
          bytesDeleted: response.getBytesDeleted(),
          deletedFilePaths: response.getDeletedFilePathsList(),
          hardDeleted: response.getHardDeleted(),
          message: response.getMessage() || 'Project deleted successfully',
        };

        ClientLoggingService.log('INFO', '🗑️ ProjectDeleteService: Mapped result', result);
        return result;
      }),
      catchError(error => {
        ClientLoggingService.log('ERROR', '🗑️ ProjectDeleteService: Error occurred', error);

        // Extract meaningful error message
        let errorMessage = 'Unknown error occurred';
        if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        const result: ProjectDeleteResult = {
          success: false,
          projectId: options.projectId,
          error: errorMessage,
        };

        ClientLoggingService.log('INFO', '🗑️ ProjectDeleteService: Error result', result);
        return throwError(() => result);
      })
    );
  }

  /**
   * Get project information before deletion (for display in dialog)
   * This could be enhanced to fetch file count and size information
   */
  getProjectDeletionInfo(projectId: string): Observable<{
    projectId: string;
    fileCount?: number;
    totalSizeBytes?: number;
    estimatedCost?: number;
  }> {
    // For now, return basic info
    // In the future, this could call a separate RPC to get detailed project stats
    return from(
      Promise.resolve({
        projectId: projectId,
        fileCount: undefined, // Could be fetched from backend
        totalSizeBytes: undefined, // Could be fetched from backend
        estimatedCost: undefined, // Future enhancement
      })
    );
  }
}
